/**
 * The shared dashboard: a 32px pane header (title, date, the page's actions,
 * the rail toggle), a centred column (hero with the scoped agent's composer,
 * attention cards) and a right rail with the live activity feed.
 * Parameterised by scope the way `AgentWorkspacePage` is, so Home and the
 * client Overview are one component.
 *
 * The rail collapses (remembered in `useDashboardLayout`); below `lg` it
 * becomes a sheet opened from an "Activity" button.
 */
import { Activity, PanelRightOpen } from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { HeaderIconButton } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { PanelAgent } from "@/features/agents/agentPanelStore";
import {
  caseyName,
  scopeKey,
  type AgentScope,
  type AgentSpec
} from "@/features/agents/types";
import { reloadActivityFeeds } from "@/features/clientProfile/tabs/activity/useActivityFeed";
import { useSeedFirmTimezone } from "@/features/lawFirms/hooks/useSeedFirmTimezone";
import { cn } from "@/lib/utils";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import { useDashboardLayout } from "../dashboardLayoutStore";
import { longDate } from "../greeting";
import type {
  ClientDashboard,
  DashboardScope,
  PracticeDashboard
} from "../types";
import { useDashboardDraft } from "../useDashboardDraft";
import { useDashboardSummary } from "../useDashboardSummary";
import { useDashboardSuggestions } from "../useDashboardSuggestions";
import { useRecommendations } from "../useRecommendations";
import { ActivityRail } from "./ActivityRail";
import { DashboardHero } from "./DashboardHero";
import { LastUpdated } from "./LastUpdated";

export const LG_QUERY = "(min-width: 1024px)";

/** Whether the viewport matches `query` (true until measured, for SSR/tests). */
export const useMatches = (query: string): boolean => {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? true
      : window.matchMedia(query).matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    setMatches(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

export type DashboardCardsState<T> = {
  summary: T | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  refresh: () => void;
  /** Send a prompt to the agent (new thread + workspace). */
  prompt: (text: string) => void;
};

type BaseProps = {
  agent: PanelAgent;
  scope: AgentScope;
  spec: AgentSpec | null;
  /** The header's title ("Your day" / "Overview"). */
  title: string;
  /** Shown after the title in the header (e.g. the client's name). */
  description?: string | null;
  /** The page's actions in the header (New Request, New Case). */
  actions?: ReactNode;
  /** Client mode: the client's first name for the greeting. */
  subject?: string | null;
  /** Rendered between the greeting and the chips (e.g. a scope chip). */
  heroExtra?: ReactNode;
  /** Cards placed above the summary-driven ones (e.g. GetStartedCard). */
  leadingCards?: ReactNode;
  className?: string;
};

export type DashboardShellProps =
  | (BaseProps & {
      mode: "practice";
      cards: (state: DashboardCardsState<PracticeDashboard>) => ReactNode;
    })
  | (BaseProps & {
      mode: "client";
      clientId: string;
      cards: (state: DashboardCardsState<ClientDashboard>) => ReactNode;
      /** Who the client is, shown in place of the greeting. */
      identity?: (state: DashboardCardsState<ClientDashboard>) => ReactNode;
    });

const FALLBACK_LABEL: Record<PanelAgent, string> = {
  practice: "Casey",
  client: "Casey"
};

/** The summary endpoint's scope for these props. */
const dashboardScopeOf = (p: DashboardShellProps): DashboardScope =>
  p.mode === "client"
    ? { kind: "client", id: p.clientId }
    : { kind: "practice", id: p.scope.id };

/** Hand the (already narrowed) state to the caller's card renderer. */
const renderCards = (
  p: DashboardShellProps,
  state: DashboardCardsState<PracticeDashboard | ClientDashboard>
): ReactNode =>
  p.mode === "client"
    ? p.cards(state as DashboardCardsState<ClientDashboard>)
    : p.cards(state as DashboardCardsState<PracticeDashboard>);

/** Client mode's identity block, when the caller gave one. */
const renderIdentity = (
  p: DashboardShellProps,
  state: DashboardCardsState<PracticeDashboard | ClientDashboard>
): ReactNode =>
  p.mode === "client" && p.identity
    ? p.identity(state as DashboardCardsState<ClientDashboard>)
    : null;

export const DashboardShell: FC<DashboardShellProps> = (props) => {
  const {
    mode,
    agent,
    scope,
    spec,
    title,
    description,
    actions,
    subject,
    heroExtra,
    leadingCards,
    className
  } = props;
  const firstName = useSelector(lawyerSelectors.selectFirstName);
  const { law_firm_id: lawFirmId } = useSelector(lawyerSelectors.selectAll);
  useSeedFirmTimezone(lawFirmId);
  const isLg = useMatches(LG_QUERY);
  const { railCollapsed, setRailCollapsed } = useDashboardLayout();
  const [railSheetOpen, setRailSheetOpen] = useState(false);

  const dashboardScope = dashboardScopeOf(props);
  const summary = useDashboardSummary(dashboardScope);
  const recs = useRecommendations(dashboardScope);
  const suggestions = useDashboardSuggestions({
    mode,
    items: recs.items,
    isLoading: recs.isLoading,
    regenerate: recs.regenerate,
    summary: summary.data,
    summaryLoading: summary.isLoading,
    dismissScope: dashboardScope.kind === "client" ? dashboardScope.id : null
  });
  const draft = useDashboardDraft(agent, scope);
  const agentLabel = caseyName(spec?.label ?? FALLBACK_LABEL[agent]);
  const [forcing, setForcing] = useState(false);

  // The header's Refresh: the summary bypasses the server cache, the
  // recommendations re-read their rules (the LLM chips keep their own
  // control) and the activity rail re-reads its first page. In parallel; only
  // a summary failure is worth a toast, and the data on screen stays.
  const { refresh: refreshSummary } = summary;
  const { reload: reloadRecs } = recs;
  const scopeKind = dashboardScope.kind;
  const scopeId = dashboardScope.id;
  const refreshAll = useCallback(async () => {
    setForcing(true);
    try {
      const [summaryResult] = await Promise.allSettled([
        refreshSummary(),
        reloadRecs(),
        reloadActivityFeeds({ kind: scopeKind, id: scopeId })
      ]);
      if (summaryResult.status === "rejected") {
        toast.error("Couldn't refresh the dashboard.", {
          description:
            summaryResult.reason instanceof Error
              ? summaryResult.reason.message
              : undefined
        });
      }
    } finally {
      setForcing(false);
    }
  }, [refreshSummary, reloadRecs, scopeId, scopeKind]);

  const showRail = !railCollapsed && isLg;
  const cardsState: DashboardCardsState<PracticeDashboard | ClientDashboard> = {
    summary: summary.data,
    isLoading: summary.isLoading,
    isRefreshing: summary.isRefreshing,
    error: summary.error,
    refresh: () => void refreshAll(),
    prompt: (text: string) => void draft.send(text)
  };
  const cards = renderCards(props, cardsState);
  const identity = renderIdentity(props, cardsState);

  const rail = (
    <ActivityRail
      source={dashboardScope}
      onCollapse={() =>
        isLg ? setRailCollapsed(true) : setRailSheetOpen(false)
      }
    />
  );

  return (
    <TooltipProvider>
      <div
        className={cn("flex h-full min-h-0 w-full bg-background", className)}
        data-testid={`dashboard-shell-${mode}`}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <header
            className="flex h-8 shrink-0 items-center gap-2 border-b border-border bg-fill-1 px-3"
            data-testid="dashboard-header"
          >
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
              <h2 className="shrink-0 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {title}
              </h2>
              {description ? (
                <span className="truncate text-xs text-fg-3">
                  {description}
                </span>
              ) : null}
              <span
                className={cn(
                  "hidden shrink-0 text-sm text-fg-3",
                  // The client's name wins over the date when the column is tight.
                  description ? "xl:inline" : "sm:inline"
                )}
                data-testid="dashboard-date"
              >
                {longDate()}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <LastUpdated
                updatedAt={summary.updatedAt}
                isFetching={summary.isRefreshing || recs.isLoading}
                isRefreshing={forcing}
                onRefresh={() => void refreshAll()}
              />
              {actions}
              {isLg ? (
                railCollapsed ? (
                  <HeaderIconButton
                    label="Show activity"
                    onClick={() => setRailCollapsed(false)}
                    data-testid="dashboard-rail-open"
                  >
                    <PanelRightOpen className="size-4" />
                  </HeaderIconButton>
                ) : null
              ) : (
                <Button
                  variant="ghost"
                  size="xs"
                  className="text-muted-foreground"
                  onClick={() => setRailSheetOpen(true)}
                  data-testid="dashboard-rail-sheet-open"
                >
                  <Activity /> Activity
                </Button>
              )}
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto flex w-full max-w-190 flex-col gap-6 px-4 pb-16 pt-8 md:px-6">
              <DashboardHero
                mode={mode}
                agent={agent}
                agentLabel={agentLabel}
                seed={scopeKey(scope)}
                firstName={firstName}
                subject={subject}
                counts={summary.data?.greeting_counts}
                countsLoading={summary.isLoading}
                recommendations={suggestions.items}
                recommendationsLoading={suggestions.isLoading}
                onRegenerate={suggestions.regenerate}
                onDismiss={suggestions.dismiss}
                onSend={(text) => void draft.send(text)}
                isSending={draft.isSending}
                identity={identity}
              >
                {heroExtra}
              </DashboardHero>

              <div
                className="flex flex-col gap-4"
                data-testid="dashboard-cards"
              >
                {leadingCards}
                {cards}
              </div>
            </div>
          </div>
        </div>

        {showRail ? (
          <aside
            className="hidden w-80 shrink-0 border-l border-border lg:flex lg:flex-col"
            data-testid="dashboard-rail"
          >
            {rail}
          </aside>
        ) : null}

        {!isLg ? (
          <Sheet open={railSheetOpen} onOpenChange={setRailSheetOpen}>
            <SheetContent side="right" className="w-80 p-0 sm:max-w-sm">
              <SheetTitle className="sr-only">Activity</SheetTitle>
              {rail}
            </SheetContent>
          </Sheet>
        ) : null}
      </div>
    </TooltipProvider>
  );
};
